'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * FormField - Reusable form field wrapper with consistent styling
 * 
 * Provides consistent label, input, and error styling across all forms.
 * Follows centralized color system (slate for neutrals, mb-blue for primary).
 * 
 * @example
 * ```tsx
 * <FormField label="Segment name" required>
 *   <input type="text" className="..." />
 * </FormField>
 * ```
 */

interface FormFieldProps {
  /**
   * Field label text
   */
  label?: string;
  
  /**
   * Whether field is required
   */
  required?: boolean;
  
  /**
   * Error message to display
   */
  error?: string;
  
  /**
   * Helper text to display below input
   */
  helperText?: string;
  
  /**
   * Field content (input, select, etc.)
   */
  children: React.ReactNode;
  
  /**
   * HTML id for the field (for label association)
   */
  id?: string;
  
  /**
   * Additional CSS classes for container
   */
  className?: string;
  
  /**
   * Additional CSS classes for label
   */
  labelClassName?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  helperText,
  children,
  id,
  className = '',
  labelClassName = '',
}) => {
  const generatedId = React.useId();
  const fieldId = id || generatedId;
  
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label 
          htmlFor={fieldId}
          className={cn('text-sm font-medium text-slate-900', labelClassName)}
        >
          {label}
          {required && <span className="text-orange-500 ml-1">*</span>}
        </label>
      )}
      {children}
      {error && (
        <p className="text-xs text-orange-700">{error}</p>
      )}
      {helperText && !error && (
        <p className="text-xs text-slate-500">{helperText}</p>
      )}
    </div>
  );
};

/**
 * FormInput - Reusable input field with consistent styling
 * 
 * @example
 * ```tsx
 * <FormInput
 *   label="Segment name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 *   required
 * />
 * ```
 */

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Field label text
   */
  label?: string;
  
  /**
   * Whether field is required
   */
  required?: boolean;
  
  /**
   * Error message to display
   */
  error?: string;
  
  /**
   * Helper text to display below input
   */
  helperText?: string;
  
  /**
   * Additional CSS classes for container
   */
  containerClassName?: string;
}

export const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      required = false,
      error,
      helperText,
      containerClassName = '',
      className = '',
      id,
      ...inputProps
    },
    ref
  ) => {
    const generatedId = React.useId();
    const fieldId = id || generatedId;
    
    return (
      <FormField
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        id={fieldId}
        className={containerClassName}
      >
        <input
          ref={ref}
          id={fieldId}
          className={cn(
            'w-full px-3 py-2.5 border rounded-lg text-sm',
            'focus:outline-none focus:ring-2 focus:ring-mb-blue-500 focus:border-transparent',
            'transition-colors',
            error
              ? 'border-orange-300 focus:ring-orange-500'
              : 'border-slate-300 focus:border-mb-blue-500',
            className
          )}
          {...inputProps}
        />
      </FormField>
    );
  }
);

FormInput.displayName = 'FormInput';

